import { z } from 'zod';

/* ─────────────────────────────────────────────────────────────────────
   O que o agente devolve: blocos tipados, nunca HTML ou Markdown.
   Cada bloco é validado no instante em que chega. O conteúdo é
   estrutura; a aparência vem do design system do cliente.

   Texto aceita uma única marcação: **ênfase**.
   ───────────────────────────────────────────────────────────────────── */

const t = (max: number) => z.string().trim().min(1).max(max);

export const sectionBlock   = z.object({ type: z.literal('section'),   id: z.string().min(1).max(40), title: t(140) });
export const headingBlock   = z.object({ type: z.literal('heading'),   level: z.union([z.literal(2), z.literal(3)]).default(3), text: t(200) });
export const paragraphBlock = z.object({ type: z.literal('paragraph'), text: t(4000) });
export const listBlock      = z.object({ type: z.literal('list'),      style: z.enum(['bullet', 'number']).default('bullet'), items: z.array(t(600)).min(1).max(30) });
export const metricBlock    = z.object({ type: z.literal('metric'),    label: t(80), value: z.union([z.number(), t(40)]), unit: z.string().max(12).optional(), delta: z.string().max(40).optional() });
export const tableBlock     = z.object({ type: z.literal('table'),     caption: z.string().max(200).optional(), columns: z.array(t(60)).min(1).max(10), rows: z.array(z.array(z.string().max(400))).max(60) });
export const chartBlock     = z.object({ type: z.literal('chart'),     kind: z.enum(['bar', 'line']), title: z.string().max(140).optional(), unit: z.string().max(12).optional(), series: z.array(z.object({ label: t(60), value: z.number() })).min(1).max(24) });
export const calloutBlock   = z.object({ type: z.literal('callout'),   tone: z.enum(['info', 'attention']).default('info'), text: t(800) });
export const quoteBlock     = z.object({ type: z.literal('quote'),     text: t(800), cite: z.string().max(120).optional() });

/* ── imagens: fotos que a organização enviou na conversa com o agente ──
   O bloco nunca carrega uma URL: aponta para um arquivo do projeto
   (asset = id do upload). O renderizador resolve o endereço — a API na
   prévia, um arquivo do pacote na publicação. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const asset = z.string().trim().regex(UUID, 'imagem: referência a um arquivo enviado');
const media = z.object({ asset, alt: z.string().trim().max(240).default('') });
export const imageBlock     = z.object({ type: z.literal('image'),     asset, alt: z.string().trim().max(240).default(''), caption: z.string().trim().max(240).optional() });

/* ── blocos de página web (landing e site) ─────────────────────────
   O mesmo princípio: estrutura, não HTML. O renderizador do site decide
   o desenho a partir do design system do cliente. */
const link = z.object({ label: t(48), href: z.string().trim().max(400).optional() });
const slug = z.string().trim().max(60).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$|^$/, 'slug: letras minúsculas, números e hífens');

export const pageBlock        = z.object({ type: z.literal('page'),        id: z.string().min(1).max(40), title: t(80), slug });
export const heroBlock        = z.object({ type: z.literal('hero'),        eyebrow: z.string().trim().max(80).optional(), title: t(160), subtitle: z.string().trim().max(500).optional(), cta: link.optional(), secondary: link.optional(), image: media.optional() });
export const featuresBlock    = z.object({ type: z.literal('features'),    title: z.string().trim().max(160).optional(), intro: z.string().trim().max(500).optional(), items: z.array(z.object({ title: t(90), text: t(500) })).min(2).max(6) });
export const stepsBlock       = z.object({ type: z.literal('steps'),       title: z.string().trim().max(160).optional(), items: z.array(z.object({ title: t(90), text: t(360) })).min(2).max(6) });
export const statsBlock       = z.object({ type: z.literal('stats'),       items: z.array(z.object({ value: t(24), label: t(90) })).min(2).max(4) });
export const testimonialBlock = z.object({ type: z.literal('testimonial'), quote: t(600), author: t(90), role: z.string().trim().max(90).optional() });
export const ctaBlock         = z.object({ type: z.literal('cta'),         title: t(160), text: z.string().trim().max(400).optional(), button: link });
export const faqBlock         = z.object({ type: z.literal('faq'),         title: z.string().trim().max(160).optional(), items: z.array(z.object({ q: t(220), a: t(900) })).min(1).max(12) });
export const galleryBlock     = z.object({ type: z.literal('gallery'),     title: z.string().trim().max(160).optional(), items: z.array(media.extend({ caption: z.string().trim().max(160).optional() })).min(2).max(8) });

export const blockSchema = z.discriminatedUnion('type', [
  sectionBlock, headingBlock, paragraphBlock, listBlock, metricBlock,
  tableBlock, chartBlock, calloutBlock, quoteBlock, imageBlock,
  pageBlock, heroBlock, featuresBlock, stepsBlock, statsBlock, testimonialBlock, ctaBlock, faqBlock, galleryBlock,
]);
export type Block = z.infer<typeof blockSchema>;
export type BlockType = Block['type'];
export type BlockOf<T extends BlockType> = Extract<Block, { type: T }>;
export const DOC_BLOCK_TYPES: readonly BlockType[] = [
  'section', 'heading', 'paragraph', 'list', 'metric', 'table', 'chart', 'callout', 'quote', 'image',
];
export const WEB_BLOCK_TYPES: readonly BlockType[] = ['page', 'hero', 'features', 'steps', 'stats', 'testimonial', 'cta', 'faq', 'gallery'];
export const BLOCK_TYPES: readonly BlockType[] = [...DOC_BLOCK_TYPES, ...WEB_BLOCK_TYPES];

/** As imagens de que um bloco depende (ids de arquivos do projeto). */
export function blockAssets(b: Block): string[] {
  if (b.type === 'image') return [b.asset];
  if (b.type === 'gallery') return b.items.map(i => i.asset);
  if (b.type === 'hero' && b.image) return [b.image.asset];
  return [];
}

/** A tabela tem de fechar: toda linha com o mesmo número de colunas. */
export function validateBlock(input: unknown): { ok: true; block: Block } | { ok: false; error: string } {
  const r = blockSchema.safeParse(input);
  if (!r.success) return { ok: false, error: r.error.issues.map(i => `${i.path.join('.') || 'bloco'}: ${i.message}`).join('; ') };
  const b = r.data;
  if (b.type === 'table' && b.rows.some(row => row.length !== b.columns.length))
    return { ok: false, error: 'table: linhas com número de colunas diferente do cabeçalho' };
  return { ok: true, block: b };
}

/* ── o esboço que o agente anuncia antes de escrever ─────────────── */
export const outlineSchema = z.object({
  title: t(160),
  sections: z.array(z.object({ id: z.string().min(1).max(40), title: t(140) })).min(1).max(16),
});
export type Outline = z.infer<typeof outlineSchema>;

/* ── eventos do stream de geração (SSE) ──────────────────────────── */
export type GenerationPhase = 'reading' | 'outlining' | 'writing' | 'finishing';

export interface GenStartEvent   { artifactId: string; tool: string; title: string; dsVersionId: string | null; dnaSnapshotId: string | null; cost: number }
export interface GenContextEvent { sources: Array<{ key: string; label: string; coverage: number }> }
export interface GenPhaseEvent   { phase: GenerationPhase; label: string }
export interface GenOutlineEvent { outline: Outline }
export interface GenBlockEvent   { index: number; block: Block }
export interface GenUsageEvent   { tokens: number }
export interface GenDoneEvent    { artifactId: string; blocks: number; tokens: number; credits: { used: number; quota: number } }
export interface GenErrorEvent   { message: string; code?: string }

export interface GenEventMap {
  start: GenStartEvent;
  context: GenContextEvent;
  phase: GenPhaseEvent;
  outline: GenOutlineEvent;
  block: GenBlockEvent;
  usage: GenUsageEvent;
  done: GenDoneEvent;
  error: GenErrorEvent;
}
export type GenEventName = keyof GenEventMap;
export type GenEvent = { [K in GenEventName]: { event: K; data: GenEventMap[K] } }[GenEventName];

/** Divide um texto em trechos simples e com **ênfase**. */
export function inlineRuns(text: string): Array<{ text: string; strong: boolean }> {
  const out: Array<{ text: string; strong: boolean }> = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0, m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ text: text.slice(last, m.index), strong: false });
    out.push({ text: m[1]!, strong: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), strong: false });
  return out;
}
